import { Component, Input, Optional, Self, ViewChild, ElementRef } from '@angular/core';
import {
  ControlValueAccessor,
  ReactiveFormsModule,
  FormsModule,
  NgControl,
} from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-custom-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './custom-input.component.html',
  styleUrl: './custom-input.component.css',
})
export class CustomInputComponent implements ControlValueAccessor {
  @ViewChild('inputElement') inputElement?: ElementRef<HTMLInputElement>;

  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() type: string = 'text';
  @Input() id: string = '';
  @Input() readonly: boolean = false;
  @Input() step?: string;
  @Input() allowDecimals: boolean = true;

  value: string = '';
  disabled: boolean = false;

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  get control() {
    return this.ngControl?.control;
  }

  get isInvalid() {
    return this.control?.invalid && (this.control?.touched || this.control?.dirty);
  }

  get errorMessage() {
    if (!this.control?.errors) return '';
    if (this.control.errors['required']) return `${this.label || 'Field'} is required`;
    return 'Invalid field';
  }

  onChange: (value: string) => void = () => { };
  onTouched: () => void = () => { };

  onKeyDown(event: KeyboardEvent): void {
    if (this.type === 'number' && !this.allowDecimals) {
      if (['.', ',', 'e', 'E'].includes(event.key)) {
        event.preventDefault();
      }
    }
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (this.type === 'number' && !this.allowDecimals) {
      if (input.value.includes('.')) {
        input.value = input.value.split('.')[0];
      }
    }
    const value = input.value;
    this.value = value;
    this.onChange(value);
  }

  writeValue(value: string): void {
    this.value = value || '';
    if (this.inputElement) {
      this.inputElement.nativeElement.value = this.value;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
